import React, { useState, useRef, useEffect } from "react"
import { OldButton } from "@kit/design/old-button"
import Modal, { Action } from "@kit/design/modal"
import { useToggle } from "hooks/use-toggle"
import cx from "classnames"
import { useCurrentNode } from "../../index"
import { useRenderContext } from "../../render-context"
import SlateNode from "../slate-node"
import Label from "@kit/design/label"
import Input from "@kit/design/input"
import Textarea from "@kit/design/textarea"
import Stack from "@kit/design/stack"
import { saveScript, getScript } from "./utils"
import { toast } from "@kit/design/toast"

function useAutoFocus() {
  const ref = useRef()

  useEffect(() => {
    setTimeout(() => {
      ref.current.focus()
    })
  }, [])

  return ref
}

function EditModal({ onSave, onCancel, initialValue }) {
  const [source, setSource] = useState("")
  const [height, setHeight] = useState(initialValue.height)
  const [version, setVersion] = useState(initialValue.version || 0)
  const id = initialValue.id
  const ref = useAutoFocus()

  useEffect(() => {
    const getSource = async () => {
      if (id) {
        try {
          const rawSource = await getScript(id)
          setSource(rawSource)
        } catch {
          toast({
            type: "error",
            text: "Failed to get script. Please try again later or contact support."
          })
        }
      }
    }
    getSource()
  }, [id])

  const handleSourceChange = event => {
    setSource(event.target.value)
  }

  const handleHeightChange = event => {
    setHeight(event.target.value)
  }

  const handleSave = async () => {
    const newVersion = version + 1
    setVersion(newVersion)
    try {
      const saved = await saveScript(id, { source })
      onSave({ scriptId: saved.id, height, version: newVersion })
    } catch {
      toast({
        type: "error",
        text: "Failed to save script. Please try again later or contact support."
      })
    }
  }
  return (
    <Modal label="Edit Script" onClose={onCancel}>
      <Stack spacing={3}>
        <div>
          <Label>Container Height (in pixels)</Label>
          <Input
            type="number"
            onChange={handleHeightChange}
            value={height || 0}
            max={1000}
          />
          <p className="text-sm text-gray-700 mt-1">
            Leave as 0 for scripts that don&apos;t need to be displayed.
          </p>
        </div>
        <div>
          <Label>Source</Label>
          <Textarea
            className="w-full h-64"
            ref={ref}
            onChange={handleSourceChange}
            value={source}
            placeholder="<script src='https://example.com/script.js'></script>"
          />
        </div>
      </Stack>
      <Action is="secondary" onClick={onCancel}>
        Cancel
      </Action>
      <Action is="primary" onClick={handleSave}>
        Save
      </Action>
    </Modal>
  )
}

export default function ScriptEditorComponent({ node, attributes, editor }) {
  const [settingsOpened, toggleSettings] = useToggle(false)
  const focusBlock = useCurrentNode()
  const focused =
    focusBlock && (focusBlock === node || node.hasDescendant(focusBlock.key))
  const { target, mode } = useRenderContext()

  const handleSave = ({ scriptId, height, version }) => {
    editor.setNodeByKey(node.key, { data: { scriptId, height, version } })
    toggleSettings()
  }

  const scriptId = node.data.get("scriptId")
  const scriptHeight = node.data.get("height")
  const scriptVersion = node.data.get("version")

  const containerClass = cx(
    "p-2 text-gray-600 group-hover:bg-gray-100 border border-gray-300/0 hover:border-gray-300/100 whitespace-normal",
    {
      "bg-gray-100 border-gray-300/100": focused
    }
  )
  const editBtnClass = cx(
    "grid place-content-center h-6 opacity-0 group-hover:opacity-100 justify-self-end",
    {
      "opacity-100": focused
    }
  )
  if (mode === "live" && target === "web") {
    return (
      <SlateNode node={node} {...attributes}>
        <LiveScript node={node} />
      </SlateNode>
    )
  }
  return (
    <SlateNode node={node} className="grid relative group" {...attributes}>
      <div
        className={containerClass}
        style={{ gridArea: "1/1/2/4" }}
        data-live={mode === "live"}
      >
        {scriptId ? "Edit Script" : "Enter Script here"}
      </div>
      <OldButton
        type="button"
        size="sm"
        is="primary"
        contentEditable={false}
        className={editBtnClass}
        style={{ gridArea: "1 / 3 / 2 / 4" }}
        onClick={e => {
          // prevents calling onClick on the entire node that would result in
          // an endless re-render loop
          e.stopPropagation()
          toggleSettings()
        }}
      >
        Edit
      </OldButton>
      {settingsOpened && (
        <EditModal
          onCancel={toggleSettings}
          onSave={handleSave}
          initialValue={{
            id: scriptId,
            height: scriptHeight,
            version: scriptVersion
          }}
        />
      )}
    </SlateNode>
  )
}

export const LiveScript = ({ node }) => {
  const scriptId = node.data.get("scriptId")
  const height = node.data.get("height") || 0
  const version = node.data.get("version") || 0
  if (!scriptId) return null

  const scriptSrc = `${process.env.NODE_FUNCTIONS_HOST}/scripts?id=${scriptId}&version=${version}`

  // In sandbox, we must add allow-same-origin to support 3rd-party scripts which need an origin for CORS and cookies
  // and without allow-same-origin, the origin is null
  return (
    <iframe
      title="Embedded Script"
      id="scriptFrame"
      height={height}
      className="w-full"
      style={{ border: 0 }}
      sandbox="allow-scripts allow-popups allow-forms allow-modals allow-same-origin"
      src={scriptSrc}
    ></iframe>
  )
}
